import { useEffect, useMemo, useState } from "react";
import {
  Search, Wallet, Clock, CheckCircle2, Info, Loader2, ChevronLeft, ChevronRight, X,
} from "lucide-react";
import { useCommissionsStore, type Commission } from "../store/useCommissionsStore";

const pkr = (n: number) => `PKR ${Math.round(n || 0).toLocaleString()}`;
const usd = (n: number) => `$${(Math.round((n || 0) * 100) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
// BD-facing status: Paid stays Paid; everything released-but-unpaid shows as Pending.
const bdStatus = (s: string) => (s === "Paid" ? "Paid" : "Pending");
const comId = (c: Commission) => `COM-${new Date(c.createdAt || Date.now()).getFullYear()}-${String(c._id).slice(-6).toUpperCase()}`;

const DATE_RANGES = [
  { key: "all", label: "All Time" }, { key: "month", label: "This Month" },
  { key: "30", label: "Last 30 Days" }, { key: "quarter", label: "Last Quarter" }, { key: "year", label: "This Year" },
];
const rangeBounds = (key: string): { from?: string } => {
  const now = new Date(); const iso = (d: Date) => d.toISOString().slice(0, 10);
  switch (key) {
    case "month": return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)) };
    case "30": { const d = new Date(); d.setDate(d.getDate() - 30); return { from: iso(d) }; }
    case "quarter": { const d = new Date(); d.setMonth(d.getMonth() - 3); return { from: iso(d) }; }
    case "year": return { from: iso(new Date(now.getFullYear(), 0, 1)) };
    default: return {};
  }
};
const PER_PAGE = 10;

export default function BDCommissionsPage() {
  const { commissions, kpis, fetchCommissions, fetchKpis, loading } = useCommissionsStore();
  const [search, setSearch] = useState("");
  const [range, setRange] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [structOpen, setStructOpen] = useState(false);
  const [view, setView] = useState<Commission | null>(null);

  useEffect(() => {
    const b = rangeBounds(range);
    fetchCommissions({ from: b.from });
    fetchKpis({ from: b.from });
  }, [fetchCommissions, fetchKpis, range]);

  const rate = commissions[0]?.ratePkrPerUsd || 10;
  const ruleText = `$100 : PKR ${(rate * 100).toLocaleString()}`;

  const filtered = useMemo(() => {
    let rows = [...commissions];
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((c) => {
      const cl: any = c.client, p: any = c.project;
      return [cl?.name, cl?.username, p?.name, c.milestoneTitle, comId(c)].some((v) => String(v || "").toLowerCase().includes(q));
    });
    if (statusFilter === "paid") rows = rows.filter((c) => c.status === "Paid");
    else if (statusFilter === "pending") rows = rows.filter((c) => c.status !== "Paid");
    rows.sort((a, b) => {
      if (sort === "highest") return (b.commissionAmount || 0) - (a.commissionAmount || 0);
      if (sort === "lowest") return (a.commissionAmount || 0) - (b.commissionAmount || 0);
      if (sort === "oldest") return new Date(a.updatedAt || 0).getTime() - new Date(b.updatedAt || 0).getTime();
      return new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime();
    });
    return rows;
  }, [commissions, search, statusFilter, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageRows = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  useEffect(() => { setPage(1); }, [search, statusFilter, range]);

  return (
    <div className="p-5 sm:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-on-surface">Commissions</h1>
        <p className="text-sm text-on-surface-variant mt-1">Track your earned, pending, and paid commissions across projects.</p>
      </div>

      {/* Search + filters */}
      <div className="bg-white rounded-xl border border-outline-variant shadow-sm p-3 mb-6 flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-outline" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by client, username, project, or milestone..." className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-background border border-outline-variant text-sm outline-none focus:border-primary" />
        </div>
        <select value={range} onChange={(e) => setRange(e.target.value)} className="px-3 py-2.5 rounded-lg border border-outline-variant text-sm bg-white">
          {DATE_RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2.5 rounded-lg border border-outline-variant text-sm bg-white">
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2.5 rounded-lg border border-outline-variant text-sm bg-white">
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="highest">Highest Commission</option>
          <option value="lowest">Lowest Commission</option>
        </select>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        <CommKpi icon={Wallet} iconCls="text-primary" active={statusFilter === "all"} onClick={() => setStatusFilter("all")}
          title="Earned Commission" value={pkr(kpis?.totalEarned || 0)} sub="Total commission earned on released milestones" />
        <CommKpi icon={Clock} iconCls="text-amber-600" active={statusFilter === "pending"} onClick={() => setStatusFilter("pending")}
          title="Pending Commission" value={pkr(kpis?.locked || 0)} sub="Awaiting payroll processing" dot />
        <CommKpi icon={CheckCircle2} iconCls="text-secondary" active={statusFilter === "paid"} onClick={() => setStatusFilter("paid")}
          title="Paid Commission" value={pkr(kpis?.paid || 0)} sub="Processed through ERP payroll" />
      </div>

      {/* Commission structure banner */}
      <div className="bg-primary-container/40 border border-primary/20 rounded-xl p-4 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <Info size={20} className="text-primary shrink-0 mt-0.5" />
          <p className="text-sm text-on-surface">
            <span className="font-bold">Current Commission Structure.</span> Your commission is calculated at{" "}
            <span className="font-bold text-primary">PKR {(rate * 100).toLocaleString()} for every $100</span> of Released Milestone Value.
            <span className="text-on-surface-variant"> Effective Since: 01 Jan 2026.</span>
          </p>
        </div>
        <button onClick={() => setStructOpen(true)} className="shrink-0 bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#004b9e]">View Commission Structure</button>
      </div>

      {/* Ledger */}
      <div className="bg-white rounded-xl border border-outline-variant shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-outline-variant"><h3 className="text-lg font-bold text-on-surface">Commission Ledger</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="bg-surface-container-low text-on-surface-variant text-left text-[11px] uppercase tracking-wide">
                <th className="p-3 font-semibold">Commission ID</th>
                <th className="p-3 font-semibold">Client</th>
                <th className="p-3 font-semibold">Project / Milestone</th>
                <th className="p-3 font-semibold text-right">Released</th>
                <th className="p-3 font-semibold text-right">Commission</th>
                <th className="p-3 font-semibold">Commission Rule</th>
                <th className="p-3 font-semibold">Status</th>
                <th className="p-3 font-semibold text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="p-10 text-center"><Loader2 className="animate-spin text-primary inline" size={22} /></td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan={8} className="p-10 text-center text-on-surface-variant">No commission records found.</td></tr>
              ) : pageRows.map((c) => {
                const cl: any = c.client, p: any = c.project;
                const st = bdStatus(c.status);
                return (
                  <tr key={c._id} className="border-b border-outline-variant last:border-0 hover:bg-surface-container-low/50">
                    <td className="p-3"><button onClick={() => setView(c)} className="font-semibold text-primary hover:underline">{comId(c)}</button></td>
                    <td className="p-3"><div className="text-on-surface">{cl?.name || "—"}</div>{cl?.username && <div className="text-[11px] text-on-surface-variant">@{cl.username}</div>}</td>
                    <td className="p-3"><div className="text-on-surface">{p?.name || "—"}</div>{c.milestoneTitle && <div className="text-[11px] text-on-surface-variant">{c.milestoneTitle}</div>}</td>
                    <td className="p-3 text-right text-on-surface">{usd(c.releasedAmount)}</td>
                    <td className="p-3 text-right font-bold text-primary">{pkr(c.commissionAmount)}</td>
                    <td className="p-3 text-[12px] text-on-surface-variant">{ruleText}</td>
                    <td className="p-3"><span className="inline-flex items-center gap-1.5 text-[12px] font-semibold"><span className={`w-1.5 h-1.5 rounded-full ${st === "Paid" ? "bg-secondary" : "bg-amber-500"}`} />{st}</span></td>
                    <td className="p-3 text-center"><button onClick={() => setView(c)} className="text-primary text-[12px] font-semibold hover:underline">View</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-5 py-4 text-sm text-on-surface-variant">
          <span>Showing {filtered.length === 0 ? 0 : (page - 1) * PER_PAGE + 1} to {Math.min(filtered.length, page * PER_PAGE)} of {filtered.length} results</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 rounded-lg border border-outline-variant inline-flex items-center gap-1 disabled:opacity-40 hover:bg-surface-container-low"><ChevronLeft size={14} /> Prev</button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 rounded-lg border border-outline-variant inline-flex items-center gap-1 disabled:opacity-40 hover:bg-surface-container-low">Next <ChevronRight size={14} /></button>
          </div>
        </div>
      </div>

      {structOpen && <StructureModal rate={rate} onClose={() => setStructOpen(false)} />}
      {view && <CommissionViewModal c={view} rate={rate} onClose={() => setView(null)} />}
    </div>
  );
}

function CommKpi({ icon: Icon, iconCls, title, value, sub, dot, active, onClick }: any) {
  return (
    <button onClick={onClick} className={`text-left bg-white rounded-xl border shadow-sm p-5 transition-all ${active ? "border-primary ring-2 ring-primary/25" : "border-outline-variant hover:border-primary/40"}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Icon size={20} className={iconCls} /><span className="text-sm font-semibold text-on-surface">{title}</span></div>
        {dot && <span className="w-2 h-2 rounded-full bg-amber-500" />}
      </div>
      <div className="text-[30px] leading-none font-bold text-on-surface">{value}</div>
      <div className="text-[12px] text-on-surface-variant mt-2">{sub}</div>
    </button>
  );
}

function StructureModal({ rate, onClose }: { rate: number; onClose: () => void }) {
  const row = (l: string, v: string) => <div className="flex justify-between py-1.5 border-b border-outline-variant last:border-0"><span className="text-on-surface-variant">{l}</span><span className="font-semibold text-on-surface">{v}</span></div>;
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant"><h2 className="text-lg font-bold text-on-surface">Commission Structure</h2><button onClick={onClose} className="p-1.5 text-outline hover:bg-surface-container rounded-full"><X size={18} /></button></div>
        <div className="p-6 text-sm space-y-1">
          {row("Commission Formula", `PKR ${(rate * 100).toLocaleString()} per $100 released`)}
          {row("Base Currency", "USD")}
          {row("Commission Currency", "PKR")}
          {row("Rate", `PKR ${rate} / USD`)}
          {row("Effective Date", "01 Jan 2026")}
          {row("Rule Status", "Active")}
          <p className="text-[12px] text-on-surface-variant pt-2">Changes to the structure apply only to future commission calculations and do not modify previously processed records.</p>
        </div>
        <div className="px-6 py-4 border-t border-outline-variant flex justify-end"><button onClick={onClose} className="bg-primary text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-[#004b9e]">Close</button></div>
      </div>
    </div>
  );
}

function CommissionViewModal({ c, rate, onClose }: { c: Commission; rate: number; onClose: () => void }) {
  const cl: any = c.client, p: any = c.project;
  const row = (l: string, v: any) => <div className="flex justify-between py-1.5 border-b border-outline-variant last:border-0"><span className="text-on-surface-variant">{l}</span><span className="font-semibold text-on-surface text-right">{v || "—"}</span></div>;
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant"><h2 className="text-lg font-bold text-on-surface">{comId(c)}</h2><button onClick={onClose} className="p-1.5 text-outline hover:bg-surface-container rounded-full"><X size={18} /></button></div>
        <div className="p-6 text-sm space-y-1">
          {row("Client", cl?.name)}
          {row("Username", cl?.username ? `@${cl.username}` : "—")}
          {row("Project", p?.name)}
          {row("Milestone", c.milestoneTitle)}
          {row("Released Amount", usd(c.releasedAmount))}
          {row("Commission Amount", pkr(c.commissionAmount))}
          {row("Applied Rule", `PKR ${(rate * 100).toLocaleString()} per $100`)}
          {row("Status", bdStatus(c.status))}
          {(c as any).payrollRef && row("Payroll Reference", (c as any).payrollRef)}
        </div>
        <div className="px-6 py-4 border-t border-outline-variant flex justify-end"><button onClick={onClose} className="bg-primary text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-[#004b9e]">Close</button></div>
      </div>
    </div>
  );
}
